import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { HeroCube } from "@/components/HeroCube";
import { LineReveal } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/hooks/useLenis";

const ease = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const { t, lang } = useLang();
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 800], [0, 180]);
  const yCube = useTransform(scrollY, [0, 800], [0, -120]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden pt-24" data-testid="hero-section">
      <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[#050505]" />
        <div className="depth-grid" />
        <div className="glow-orb w-[520px] h-[520px] bg-[#00f2fe]/[0.13] top-[10%] right-[5%]" />
        <div className="glow-orb w-[420px] h-[420px] bg-[#00f2fe]/[0.06] bottom-[-10%] left-[-5%]" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-[#050505]" />
      </motion.div>

      <div className="mx-auto max-w-7xl w-full px-6 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease, delay: 0.3 }}
            className="eyebrow mb-8"
            data-testid="hero-badge"
          >
            {t.hero.badge}
          </motion.div>

          <h1
            key={lang}
            className="font-display font-extrabold tracking-[-0.02em] leading-[1.05] text-3xl sm:text-4xl lg:text-5xl xl:text-[3.3rem] text-[#f4f2ec]"
            data-testid="hero-title"
          >
            <LineReveal delay={0.45}>{t.hero.line1}</LineReveal>
            <LineReveal delay={0.6}>
              <span className="text-gold-gradient">{t.hero.line2}</span>
            </LineReveal>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 0.9 }}
            className="mt-9 max-w-xl text-sm sm:text-base text-[#a3a3a3] leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 1.05 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button className="btn-gold" onClick={() => scrollToId("catalog")} data-testid="hero-primary-cta">
              {t.hero.cta1} <ArrowRight size={16} />
            </button>
            <button className="btn-ghost" onClick={() => scrollToId("about")} data-testid="hero-secondary-cta">
              {t.hero.cta2}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.4 }}
            className="mt-16 flex items-center gap-4 sm:gap-10"
            data-testid="hero-stats"
          >
            {[["stat1", "stat1d"], ["stat2", "stat2d"], ["stat3", "stat3d"]].map(([v, d], i) => (
              <div key={v} className="flex items-center gap-4 sm:gap-10">
                {i > 0 && <div className="stat-divider" />}
                <div>
                  <div className="font-display font-bold text-lg sm:text-2xl text-[#f4f2ec] whitespace-nowrap">{t.hero[v]}</div>
                  <div className="font-mono text-[0.65rem] tracking-[0.18em] uppercase text-[#666] mt-1">{t.hero[d]}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          style={{ y: yCube }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, ease, delay: 0.5 }}
          className="lg:col-span-5 relative h-[320px] sm:h-[400px] lg:h-[540px]"
        >
          <div className="absolute inset-0 rounded-full bg-[#00f2fe]/[0.08] blur-[100px] scale-[0.6]" />
          <HeroCube />
        </motion.div>
      </div>

      <motion.button
        style={{ opacity: fade }}
        onClick={() => scrollToId("catalog")}
        className="hidden xl:flex absolute bottom-8 right-10 flex-col items-center gap-2 text-[#666] hover:text-[#00f2fe] transition-colors"
        data-testid="hero-scroll-indicator"
        aria-label="Scroll"
      >
        <span className="font-mono text-[0.6rem] tracking-[0.3em] uppercase">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
};
